// External imports
import { useState } from "react";
import { AppBar, Toolbar, Box, Container } from "@mui/material";
import HeaderMenuButton from "../../components/HeaderMenuButton.js";
import AddIcon from "@mui/icons-material/Add";
import SortIcon from "@mui/icons-material/Sort";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Internal imports
import HideOnScroll from "../../components/HideOnScroll.js";
import SearchFilter from "../../components/SearchFilter.js";
import CollectionDialog from "./CollectionDialog.js";
import {
  getContentOptions,
  getContentOptionsArray,
  getListViewOptions,
} from "../../utils/utils.js";
import { getCollectionSortByOptions } from "../../utils/collectionUtils.js";
import ListMenu from "../../components/ListMenu.js";
import SortMenu from "../../components/SortMenu.js";
import { HeaderButton } from "../../components/CustomMUI.js";
import { useAuth } from "../../context/authContext.js";

const CollectionsHeader = ({
  toggleDrawer,
  contentType,
  onContentChange,
  searchTerm,
  sortBy,
  onSortByChange,
  sortDescending,
  toggleSortDescending,
  view,
  setView,
  onSearchTermChange,
  setNewCollectionId,
  drawerWidth,
  createListDialogOpen,
  setCreateListDialogOpen,
  viewingOtherUser = false,
}) => {
  const { user, openAuthDialog } = useAuth();
  const [contentAnchorEl, setContentAnchorEl] = useState(null);
  const [listViewAnchorEl, setListViewAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const contentOptions = getContentOptions({ showEverythingOption: true });
  const contentOptionsArray = getContentOptionsArray({
    showEverythingOption: true,
    showMixedOption: true,
  }).map((option) => ({
    ...option,
    label: option.labelPlural,
  }));
  const sortByOptions = getCollectionSortByOptions();
  const listViewOptions = getListViewOptions();
  const currentView = listViewOptions.find((option) => option.value === view);
  const currentSortBy = sortByOptions.find((option) => option.value === sortBy);

  const handleAddListOpen = () => {
    if (!user) {
      openAuthDialog("Creating collections");
    } else {
      setCreateListDialogOpen(true);
    }
  };

  const handleAddListClose = () => setCreateListDialogOpen(false);
  const handleContentClick = (event) => {
    setContentAnchorEl(event.currentTarget);
  };
  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };
  const handleTypeClick = (event) => {
    setListViewAnchorEl(event.currentTarget);
  };
  const handleContentSelect = (option) => (event) => {
    // This function needs to accept an event even though it is not used as that is how the onClick prop of the listMenu component is configured
    onContentChange(option);
    setContentAnchorEl(null);
  };

  const handleTypeSelect = (option) => (event) => {
    // This function needs to accept an event even though it is not used as that is how the onClick prop of the listMenu component is configured
    if (!user) {
      openAuthDialog("Saving collections");
      setListViewAnchorEl(null);
    } else {
      setView(option);
      setListViewAnchorEl(null);
    }
  };

  const handleSortSelect = (option) => {
    onSortByChange(null, option);
    setSortAnchorEl(null);
  };

  const handleAddNew = (listId) => {
    setNewCollectionId(listId);
    // Navigate to mine view after creating so the new collection is visible
    setView("mine");
  };

  return (
    <>
      <Toolbar sx={{ mt: 2.5, mb: 2.5 }} />
      {/* The above toolbar acts as a spacer, whatever padding is applied to the below appbar
      needs to be applied as margin to the above spacer, added 0.5 to give space to show librarycard hover scale effects */}
      <HideOnScroll>
        <AppBar
          color="inherit"
          sx={{
            boxShadow: "none",
            backgroundImage: "none",
            width: { md: `calc(100% - ${drawerWidth}px )` },
            ml: { md: `${drawerWidth}px` },
            pt: 2,
            pb: 2,
            overflowX: "auto",
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters>
              <HeaderMenuButton onClick={toggleDrawer} sx={{ mr: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  flexGrow: 1,
                  flexShrink: 0,
                  justifyContent: "flex-start",
                }}
              >
                {/* Icon-only square via HeaderButton's `mobile` styling */}
                <HeaderButton
                  mobile
                  startIcon={<AddIcon />}
                  onClick={handleAddListOpen}
                  aria-label="Create collection"
                  sx={{ mr: 1 }}
                />

                {!viewingOtherUser && (
                  <HeaderButton
                    startIcon={currentView.icon}
                    endIcon={<ExpandMoreIcon />}
                    onClick={handleTypeClick}
                    sx={{ mr: 1 }}
                  >
                    {currentView.label}
                  </HeaderButton>
                )}

                <HeaderButton
                  startIcon={contentOptions[contentType].icon}
                  endIcon={<ExpandMoreIcon />}
                  onClick={handleContentClick}
                  sx={{ mr: 1 }}
                >
                  {contentOptions[contentType].labelPlural}
                </HeaderButton>

                {view === "mine" && (
                  <HeaderButton
                    startIcon={
                      <SortIcon
                        style={{
                          // Flipped vertically for ascending sort
                          transform: sortDescending ? "none" : "scaleY(-1)",
                          transition: "transform 0.2s ease",
                        }}
                      />
                    }
                    endIcon={<ExpandMoreIcon />}
                    onClick={handleSortClick}
                    sx={{ mr: 1 }}
                  >
                    {currentSortBy.label}
                  </HeaderButton>
                )}
              </Box>

              <SearchFilter
                searchTerm={searchTerm}
                onSearchTermChange={onSearchTermChange}
                alignEnd
              />
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {/* Popups live outside the Toolbar so SearchFilter (and its trailing
      scroll spacer) stays the last item in the scrollable header row */}
      {contentAnchorEl && (
        <ListMenu
          anchorEl={contentAnchorEl}
          onClose={() => setContentAnchorEl(null)}
          onSelect={handleContentSelect}
          options={contentOptionsArray}
          selectedValue={contentType}
        />
      )}

      {!viewingOtherUser && listViewAnchorEl && (
        <ListMenu
          anchorEl={listViewAnchorEl}
          onClose={() => setListViewAnchorEl(null)}
          onSelect={handleTypeSelect}
          options={listViewOptions}
          selectedValue={view}
        />
      )}

      {sortAnchorEl && (
        <SortMenu
          anchorEl={sortAnchorEl}
          onClose={() => setSortAnchorEl(null)}
          onSelect={handleSortSelect}
          sortByValue={sortBy}
          toggleValue={sortDescending}
          onToggle={toggleSortDescending}
          sortByOptions={sortByOptions}
        />
      )}

      <CollectionDialog
        open={createListDialogOpen}
        onClose={handleAddListClose}
        onAdd={handleAddNew}
        contentType={contentType}
      />
    </>
  );
};

export default CollectionsHeader;
